import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { perfectPaymentApi } from '../api';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiPlus, FiUpload } from 'react-icons/fi';

const emptyForm = { methodId: '', methodName: '', logoUrl: '', countryCode: 'global', sortOrder: 0 };

export default function PerfectPaymentMethodsPage() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.success && data.data?.url) {
        setForm(prev => ({ ...prev, logoUrl: data.data.url }));
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fetchMethods = async () => {
    try {
      const { data } = await perfectPaymentApi.list();
      setMethods(data.methods || []);
    } catch (err) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMethods(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (method) => {
    setEditing(method);
    setForm({
      methodId: method.method_id,
      methodName: method.method_name,
      logoUrl: method.logo_url,
      countryCode: method.country_code,
      sortOrder: method.sort_order
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await perfectPaymentApi.update(editing.id, form);
        toast.success('Method updated');
      } else {
        await perfectPaymentApi.create(form);
        toast.success('Method added');
      }
      setShowModal(false);
      fetchMethods();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save method';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await perfectPaymentApi.toggle(id);
      toast.success('Status toggled');
      fetchMethods();
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const handleDelete = async (method) => {
    if (!confirm(`Delete "${method.method_name}" (ID: ${method.method_id})?`)) return;
    try {
      await perfectPaymentApi.delete(method.id);
      toast.success('Method deleted');
      fetchMethods();
    } catch (err) {
      toast.error('Failed to delete method');
    }
  };

  const handleRegenerate = async () => {
    try {
      await perfectPaymentApi.regenerateStatic();
      toast.success('Static JS file regenerated');
    } catch (err) {
      toast.error('Failed to regenerate static JS');
    }
  };

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Payment Methods</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage payment methods for Perfect Panel (smmpanelone.com)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRegenerate} className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-secondary h-10 px-4 py-2 gap-2">
            Regenerate JS
          </button>
          <button onClick={openAdd} className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2">
            <FiPlus size={16} /> Add Method
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground w-10">#</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Method ID</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Logo</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Name</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Country</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Status</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Order</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {methods.map((m, i) => (
                  <tr key={m.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="p-4 align-middle text-muted-foreground">{i + 1}</td>
                    <td className="p-4 align-middle font-mono text-xs font-medium text-foreground">{m.method_id}</td>
                    <td className="p-4 align-middle">
                      <img src={m.logo_url} alt={m.method_name} className="w-10 h-8 object-contain rounded" onError={(e) => { e.target.style.display = 'none' }} />
                    </td>
                    <td className="p-4 align-middle font-medium text-foreground">{m.method_name}</td>
                    <td className="p-4 align-middle">
                      {m.country_code && m.country_code !== 'global' ? (
                        <span className="inline-flex items-center gap-1.5">
                          <img src={`https://flagcdn.com/24x18/${m.country_code.toLowerCase()}.png`} alt={m.country_code} className="w-5 h-3.5 rounded-sm object-cover" />
                          <span className="text-xs text-muted-foreground uppercase">{m.country_code}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Global</span>
                      )}
                    </td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        m.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {m.status}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-muted-foreground text-xs">{m.sort_order}</td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleToggle(m.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors" title="Toggle active/inactive">
                          {m.status === 'active' ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                        </button>
                        <button onClick={() => openEdit(m)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors" title="Edit">
                          <FiEdit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(m)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Delete">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {methods.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      No payment methods added yet. Click "Add Method" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {editing ? 'Edit Payment Method' : 'Add Payment Method'}
              </h3>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Method ID *</label>
                <input type="text" required value={form.methodId} onChange={e => setForm({ ...form, methodId: e.target.value })}
                  className={inputClass} placeholder="e.g. 209587" disabled={!!editing} />
                <p className="text-xs text-muted-foreground">Must match the payment gateway ID from your payment provider</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Method Name *</label>
                <input type="text" required value={form.methodName} onChange={e => setForm({ ...form, methodName: e.target.value })}
                  className={inputClass} placeholder="e.g. PhonePe 3" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Logo URL *</label>
                <div className="flex gap-2">
                  <input type="url" required value={form.logoUrl} onChange={e => setForm({ ...form, logoUrl: e.target.value })}
                    className={inputClass + ' flex-1'} placeholder="https://example.com/logo.png" />
                  <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
                  <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 border border-input bg-background hover:bg-secondary h-10 px-3 gap-1.5">
                    <FiUpload size={15} /> {uploading ? '...' : 'Upload'}
                  </button>
                </div>
                {form.logoUrl && (
                  <img src={form.logoUrl} alt="preview" className="mt-1 h-8 object-contain rounded border border-border"
                    onError={(e) => { e.target.style.display = 'none' }} />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Country Code</label>
                <input type="text" value={form.countryCode} onChange={e => setForm({ ...form, countryCode: e.target.value })}
                  className={inputClass} placeholder="IN, BD, VN, EG, PH, or 'global'" />
                <p className="text-xs text-muted-foreground">Use ISO 2-letter code (IN, BD, US, etc.) or 'global' for globe icon</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Sort Order</label>
                <input type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                  className={inputClass} placeholder="0" />
              </div>
              {form.logoUrl && (
                <div className="p-3 rounded-md bg-muted/30 border border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Preview</p>
                  <div className="method-preview" style={{
                    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                    padding: '12px', background: '#fff', borderRadius: '12px', width: '120px',
                    border: '1px solid var(--border)'
                  }}>
                    <img src={form.logoUrl} alt="" style={{ width: '60px', maxHeight: '24px', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.display = 'none' }} />
                    <span style={{ marginTop: '8px', fontSize: '13px', fontWeight: 'bold', color: '#111' }}>{form.methodName || 'Name'}</span>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-secondary h-10 px-4 py-2">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Method'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
